import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import ScrollReveal from './ScrollReveal';
import HeroVisual from './HeroVisual';
import HubSpotForm from './HubSpotForm';
import { HUBSPOT_PORTAL_ID, HUBSPOT_FORM_ID, HUBSPOT_REGION } from '../config/hubspot';

const BookCallButton = ({ className = 'btn-primary-cta', children = 'Get in touch' }) => (
    <a
        href="#contact"
        className={className}
        onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
    >
        {children}
        <span className="cta-arrow" aria-hidden="true">→</span>
    </a>
);

function Home() {
    const location = useLocation();
    useEffect(() => {
        if (location.state?.scrollTo) {
            const el = document.getElementById(location.state.scrollTo);
            if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        }
    }, [location.state]);

    return (
        <main>
            <Helmet>
                <title>SRL Advisor — Measuring how students actually study</title>
                <meta name="description" content="SRL Advisor is a Moodle-integrated research instrument that links self-reported self-regulated learning to in-context evidence of study behaviour and outcomes. PhD research, University of St Andrews." />
                <link rel="canonical" href="https://srladvisor.com/" />
            </Helmet>
            {/* =========================================================
                HERO
                ========================================================= */}
            <section className="site-section-lg hero">
                <div className="hero-bg" aria-hidden="true">
                    <div className="hero-bg-blob hero-bg-blob--a" />
                    <div className="hero-bg-blob hero-bg-blob--b" />
                </div>
                <Container className="position-relative">
                    <Row className="align-items-center g-5">
                        <Col lg={6}>
                            <ScrollReveal animation="fade-up">
                                <span className="eyebrow-pill">
                                    <span className="eyebrow-dot" /> For Moodle &middot; PhD research at St Andrews
                                </span>
                                <h1 className="hero-headline mt-4 mb-4">
                                    See how your students <span className="hl hl-mint">actually</span> learn — not just what they get graded on.
                                </h1>
                                <p className="hero-sub mb-4">
                                    A Moodle plugin that turns the cycle behind every grade into
                                    evidence. For your students, and for you.
                                </p>
                                <div className="hero-ctas">
                                    <BookCallButton />
                                    <a
                                        href="#how"
                                        className="btn-secondary-cta"
                                        onClick={(e) => { e.preventDefault(); document.getElementById('how')?.scrollIntoView({ behavior: 'smooth' }); }}
                                    >
                                        How it works
                                    </a>
                                </div>
                                <ul className="hero-trust">
                                    <li><span className="trust-tick">✓</span> Moodle plugin</li>
                                    <li><span className="trust-tick">✓</span> Consent-gated</li>
                                    <li><span className="trust-tick">✓</span> No PII leaves your LMS</li>
                                </ul>
                            </ScrollReveal>
                        </Col>
                        <Col lg={6}>
                            <ScrollReveal animation="fade-up" delay={120}>
                                <HeroVisual />
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* =========================================================
                TRUST STRIP
                ========================================================= */}
            <section className="trust-strip">
                <Container>
                    <div className="trust-strip-inner">
                        <span className="trust-item"><span className="trust-emoji" aria-hidden="true">🎓</span>PhD research, University of St Andrews</span>
                        <span className="trust-divider" />
                        <span className="trust-item"><span className="trust-emoji" aria-hidden="true">📊</span>Built on the validated MSLQ instrument</span>
                        <span className="trust-divider" />
                        <span className="trust-item"><span className="trust-emoji" aria-hidden="true">🔒</span>Privacy-by-design, pseudonymous from day one</span>
                    </div>
                </Container>
            </section>

            {/* =========================================================
                #how — Three loops + plugin flow
                ========================================================= */}
            <section id="how" className="site-section">
                <Container>
                    <ScrollReveal animation="fade-up">
                        <div className="text-center mx-auto" style={{ maxWidth: 740 }}>
                            <span className="eyebrow-tag">How it works</span>
                            <h2 className="section-heading mt-3 mb-3">
                                Three windows on every student.
                            </h2>
                            <p className="section-subheading">
                                The cycle every student moves through every unit. We capture all three:
                                lightly, in context, only with consent.
                            </p>
                        </div>
                    </ScrollReveal>

                    <Row className="g-4 mt-4">
                        {[
                            {
                                badge: 'Plan',
                                tone: 'mint',
                                emoji: '🧭',
                                title: 'Forethought',
                                body: 'A 30-second pre-unit prompt: which strategy do you plan to use? Sets intention and gives us the "before".',
                            },
                            {
                                badge: 'Do',
                                tone: 'sun',
                                emoji: '✏️',
                                title: 'Performance',
                                body: 'Lightweight engagement signals: scroll depth, video pacing, completion. Behaviour, never content.',
                            },
                            {
                                badge: 'Reflect',
                                tone: 'coral',
                                emoji: '💭',
                                title: 'Self-reflection',
                                body: "A post-unit prompt: what worked, what didn't? Paired with the forethought, this is the strategy-transition picture.",
                            },
                        ].map((card, i) => (
                            <Col md={4} key={card.title} className="d-flex">
                                <ScrollReveal animation="fade-up" delay={i * 80} className="w-100 d-flex">
                                    <div className={`feature-card feature-card--${card.tone} w-100`}>
                                        <div className={`feature-emoji feature-emoji--${card.tone}`}>{card.emoji}</div>
                                        <span className={`feature-badge feature-badge--${card.tone}`}>{card.badge}</span>
                                        <h3 className="feature-title">{card.title}</h3>
                                        <p className="feature-body">{card.body}</p>
                                    </div>
                                </ScrollReveal>
                            </Col>
                        ))}
                    </Row>

                </Container>
            </section>

            {/* =========================================================
                #research — Research-backed roots
                ========================================================= */}
            <section id="research" className="site-section site-section--tinted">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={10} xl={9}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-5">
                                    <span className="eyebrow-tag eyebrow-tag--coral">Research roots</span>
                                    <h2 className="section-heading mt-3 mb-3">
                                        Standing on 30 years of evidence, not a new theory.
                                    </h2>
                                    <p className="section-subheading mx-auto" style={{ maxWidth: 720 }}>
                                        Self-regulated learning is a cycle: <em>plan, do, reflect</em>.
                                        Students who move through it deliberately outperform those who don&rsquo;t,
                                        controlling for prior ability.
                                    </p>
                                </div>
                            </ScrollReveal>

                            <Row className="g-4">
                                {[
                                    {
                                        title: "Zimmerman&rsquo;s SRL cycle",
                                        body: 'Forethought &rarr; Performance &rarr; Self-reflection. The canonical framework for thinking about how a student engages with one unit of learning.',
                                    },
                                    {
                                        title: 'The MSLQ instrument',
                                        body: "Pintrich&rsquo;s Motivated Strategies for Learning Questionnaire is the most widely validated SRL self-report instrument. We use the motivation subscales from its short revised form (MSLQ-SFR).",
                                    },
                                    {
                                        title: 'The measurement gap',
                                        body: 'Most LMS deployments only see what was graded. The cycle that produced the grade (planning, strategy choice, reflection) is invisible. SRL Advisor exists to close that gap.',
                                    },
                                ].map((c) => (
                                    <Col md={4} key={c.title} className="d-flex">
                                        <ScrollReveal animation="fade-up" className="w-100 d-flex">
                                            <div className="research-card w-100">
                                                <h4 className="research-card-title" dangerouslySetInnerHTML={{ __html: c.title }} />
                                                <p className="research-card-body" dangerouslySetInnerHTML={{ __html: c.body }} />
                                            </div>
                                        </ScrollReveal>
                                    </Col>
                                ))}
                            </Row>

                        </Col>
                    </Row>
                </Container>
            </section>

            {/* =========================================================
                MID-PAGE CTA STRIPE
                ========================================================= */}
            <section className="midcta">
                <Container>
                    <div className="midcta-inner">
                        <div className="midcta-text">
                            <h3 className="midcta-headline">
                                Want this for your institution?
                            </h3>
                            <p className="midcta-sub">
                                Pilot or research study. Both start with a 20-minute meeting.
                            </p>
                        </div>
                        <BookCallButton className="midcta-btn" />
                    </div>
                </Container>
            </section>

            {/* =========================================================
                #roadmap — Where we are / where we are going
                ========================================================= */}
            <section id="roadmap" className="site-section">
                <Container>
                    <ScrollReveal animation="fade-up">
                        <div className="text-center mx-auto mb-5" style={{ maxWidth: 740 }}>
                            <span className="eyebrow-tag eyebrow-tag--sun">Where we are</span>
                            <h2 className="section-heading mt-3 mb-3">
                                We&rsquo;re in build, heading into our first field deployment.
                            </h2>
                            <p className="section-subheading">
                                Three phases, in plain language. We&rsquo;re actively looking for institutional
                                partners for Phase 2 onward.
                            </p>
                        </div>
                    </ScrollReveal>

                    <Row className="g-4">
                        {[
                            {
                                phase: 'Phase 1',
                                state: 'In progress',
                                tone: 'mint',
                                title: 'Build &amp; validate',
                                bullets: [
                                    'Plugin, dashboard and check-ins working end-to-end',
                                    'Ethics application in process',
                                ],
                            },
                            {
                                phase: 'Phase 2',
                                state: 'Autumn 2026',
                                tone: 'sun',
                                title: 'First field deployment',
                                bullets: [
                                    'Single taught programme, one Moodle instance, descriptive and opt-in',
                                    'First paired self-report, behaviour, and outcome dataset (pending ethics)',
                                ],
                            },
                            {
                                phase: 'Phase 3',
                                state: 'Future',
                                tone: 'coral',
                                title: 'Multi-institution &amp; intervention studies',
                                bullets: [
                                    'Parallel deployments at additional partner institutions',
                                    'Evidence-gated adaptive prompts; open call for research collaborations',
                                ],
                            },
                        ].map((p, i) => (
                            <Col md={4} key={p.phase} className="d-flex">
                                <ScrollReveal animation="fade-up" delay={i * 80} className="w-100 d-flex">
                                    <div className={`roadmap-card roadmap-card--${p.tone} w-100`}>
                                        <div className="roadmap-head">
                                            <span className={`roadmap-phase roadmap-phase--${p.tone}`}>{p.phase}</span>
                                            <span className="roadmap-state" dangerouslySetInnerHTML={{ __html: p.state }} />
                                        </div>
                                        <h3 className="roadmap-title" dangerouslySetInnerHTML={{ __html: p.title }} />
                                        <ul className="roadmap-bullets">
                                            {p.bullets.map((b, j) => (
                                                <li key={j} dangerouslySetInnerHTML={{ __html: b }} />
                                            ))}
                                        </ul>
                                    </div>
                                </ScrollReveal>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* =========================================================
                #institutions — Two paths
                ========================================================= */}
            <section id="institutions" className="site-section site-section--tinted">
                <Container>
                    <ScrollReveal animation="fade-up">
                        <div className="text-center mx-auto mb-5" style={{ maxWidth: 760 }}>
                            <span className="eyebrow-tag eyebrow-tag--mint">For institutions</span>
                            <h2 className="section-heading mt-3 mb-3">
                                If your institution wants better visibility into how students learn, let&rsquo;s talk.
                            </h2>
                            <p className="section-subheading">
                                Two ways to engage. Both start with a 20-minute meeting. Both free during early access.
                            </p>
                        </div>
                    </ScrollReveal>

                    <Row className="g-4 justify-content-center align-items-stretch">
                        <Col md={6} lg={5} className="d-flex">
                            <ScrollReveal animation="fade-up" className="w-100 d-flex">
                                <div className="institution-card institution-card--mint w-100">
                                    <span className="institution-badge institution-badge--mint">For schools &amp; universities</span>
                                    <h3 className="institution-title">Run a pilot at your institution</h3>
                                    <p className="institution-body">
                                        Pick one course. We install, your students opt in, and you get a
                                        low-friction picture of how your cohort actually engages.
                                    </p>
                                    <ul className="institution-list">
                                        <li>Aggregate study-behaviour patterns by week, unit, cohort</li>
                                        <li>Privacy-by-design architecture with data management plan on file</li>
                                    </ul>
                                    <BookCallButton className="btn-primary-cta btn-primary-cta--mint" />
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={6} lg={5} className="d-flex">
                            <ScrollReveal animation="fade-up" delay={120} className="w-100 d-flex">
                                <div className="institution-card institution-card--coral w-100">
                                    <span className="institution-badge institution-badge--coral">For researchers</span>
                                    <h3 className="institution-title">Run a study with us</h3>
                                    <p className="institution-body">
                                        Bring a hypothesis. SRL Advisor supplies the instrument, the data
                                        pipeline, and the privacy posture. Co-authorship encouraged.
                                    </p>
                                    <ul className="institution-list">
                                        <li>Single- or multi-institution designs welcome</li>
                                        <li>Instrument is MSLQ-SFR + check-ins + engagement traces</li>
                                    </ul>
                                    <BookCallButton className="btn-primary-cta btn-primary-cta--coral" />
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* =========================================================
                #about — Who you'll be talking to
                ========================================================= */}
            <section id="about" className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={10} xl={9}>
                            <div className="about-shell">
                                <Row className="g-4 align-items-center">
                                    <Col md={4} className="text-center text-md-start">
                                        <ScrollReveal animation="fade-up">
                                            <div className="about-avatar">
                                                <img
                                                    src={`${process.env.PUBLIC_URL || ''}/andrew.jpg`}
                                                    srcSet={`${process.env.PUBLIC_URL || ''}/andrew-240.jpg 240w, ${process.env.PUBLIC_URL || ''}/andrew.jpg 480w`}
                                                    sizes="(max-width: 767px) 110px, 160px"
                                                    alt="Andrew Schwabe"
                                                    width={160}
                                                    height={160}
                                                    loading="lazy"
                                                />
                                            </div>
                                        </ScrollReveal>
                                    </Col>
                                    <Col md={8}>
                                        <ScrollReveal animation="fade-up" delay={80}>
                                            <span className="eyebrow-tag eyebrow-tag--mint">Who you&rsquo;ll be talking to</span>
                                            <h2 className="section-heading mt-3 mb-3">
                                                Andrew Schwabe
                                            </h2>
                                            <p className="about-role">
                                                25+ years in industry &middot; PhD researcher, University of St Andrews &middot; Educational psychology meets AI
                                            </p>
                                            <p className="about-body">
                                                Andrew spent 25+ years in industry as a founder, operator,
                                                and behavioural-analytics practitioner before starting
                                                doctoral research at the University of St Andrews.
                                                His work is interdisciplinary by design: educational
                                                psychology (specifically, self-regulated learning) meeting
                                                artificial intelligence. SRL Advisor is the applied side of
                                                that research.
                                            </p>
                                            <p className="about-links">
                                                <a
                                                    href="https://research-portal.st-andrews.ac.uk/en/persons/andrew-schwabe/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="about-link-pill"
                                                >
                                                    St Andrews research page <span aria-hidden="true">↗</span>
                                                </a>
                                                <a
                                                    href="https://scholar.google.com/citations?user=zBPTh_8AAAAJ&hl=en"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="about-link-pill about-link-pill--quiet"
                                                >
                                                    Google Scholar <span aria-hidden="true">↗</span>
                                                </a>
                                                <a
                                                    href="https://www.linkedin.com/in/aschwabe/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="about-link-pill about-link-pill--quiet"
                                                >
                                                    LinkedIn <span aria-hidden="true">↗</span>
                                                </a>
                                            </p>
                                        </ScrollReveal>
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* =========================================================
                #contact — Book a meeting + form fallback
                ========================================================= */}
            <section id="contact" className="site-section contact-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={10} xl={9}>
                            <div className="contact-shell">
                                <Row className="g-5 align-items-center">
                                    <Col lg={6}>
                                        <ScrollReveal animation="fade-up">
                                            <span className="eyebrow-tag">Get in touch</span>
                                            <h2 className="section-heading mt-3 mb-3">
                                                Let&rsquo;s set up 20 minutes.
                                            </h2>
                                            <p className="section-subheading mb-4">
                                                A short video meeting (Google Meet or your tool of choice) about
                                                your institution, your Moodle setup, and what you&rsquo;d want to
                                                learn. No pitch, no deck. Just whether a pilot or study
                                                makes sense.
                                            </p>
                                            <p className="contact-or">
                                                Use the form &rarr;, or email <a href="mailto:hello@srladvisor.com">hello@srladvisor.com</a>
                                            </p>
                                        </ScrollReveal>
                                    </Col>
                                    <Col lg={6}>
                                        <ScrollReveal animation="fade-up" delay={120}>
                                            <div className="contact-form-shell">
                                                <HubSpotForm
                                                    portalId={HUBSPOT_PORTAL_ID}
                                                    formId={HUBSPOT_FORM_ID}
                                                    region={HUBSPOT_REGION}
                                                    pageName="SRL Advisor — Contact"
                                                    submitLabel="Send"
                                                    successMessage="Thanks! We'll be in touch soon."
                                                    fields={[
                                                        { name: 'firstname', label: 'First name' },
                                                        { name: 'lastname', label: 'Last name' },
                                                        { name: 'email', label: 'Work email', type: 'email', required: true },
                                                        { name: 'jobtitle', label: 'Your role', placeholder: 'e.g. Director of Digital Learning' },
                                                        { name: 'company', label: 'Institution' },
                                                        { name: 'message', label: 'What would you like to explore?', type: 'textarea', rows: 4 },
                                                    ]}
                                                />
                                            </div>
                                        </ScrollReveal>
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </main>
    );
}

export default Home;
